// Core packages
import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormGroup } from "@angular/forms";

// Third party packages
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material";
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";

// Custom packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { PropertiesService } from "src/app/modules/properties/properties.service";
import { IUser } from "src/app/shared/models/user/user.interface";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";

import moment from 'moment';

/**
 * Script start
 */

@Component({
  selector: "app-property-detail-document-item-print-dialog",
  templateUrl: "./document-item-print-dialog.component.html",
  styleUrls: ["./document-item-print-dialog.component.scss"]
})
export class PropertyDetailDocumentItemPrintDialogComponent
  implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[];
  lang = this.translateService.currentLang || this.translateService.defaultLang;
  dialogIsLoading = false; // Used to view/hide form loading spinner

  // BEGIN - Edit item form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  // END - ngx-formly
  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.save"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-save",
      inline: true
    }
  };
  // END - Edit item form
  existingNotes: any[] = [];
  barCode = "";
  qrcodeData = "";

  year: string;

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<PropertyDetailDocumentItemPrintDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public configService: ConfigService,
    private propertiesService: PropertiesService,
    private translateService: TranslateService,
    private authService: AuthService,
    private cookieService: CookieService,
    private dialog: MatDialog,
    private toastrService: ToastrService,
    private helperService: HelperService,
  ) { }

  /**
   * Init components
   *
   * @since 1.0.0
   */
  ngOnInit(): void {

    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe(res => (this.loggedUser = res))
    );
    this.subscriptions.push(
      this.authService.loggedUserPermissions$.subscribe(
        res => (this.loggedUserPermissions = res)
      )
    );

    // Get current lang
    this.lang =
      this.cookieService.get("lang") || this.configService.defaultLang;

    // Get current year
    this.year = moment().year().toString();

    // Init form
    this.initForm();

    this.barCode = this.data.barCode;
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Init form
   *
   * @since 1.0.0
   */
  initForm(): void {
    if (this.data) {
      // QRcode.
      // Attention: not per "contratti"
      const section = this.data.documentTypeRoute;
      const host = `${this.configService.frontEndHost}`;
      const documentId = this.data.documentId ? this.data.documentId : 'generic';
      if (
        section !== "locazione-contrdi-locazione" && 
        section !== "contratti" && 
        section !== "locazioneContrdiLocazione"
      ) {
        if (section === "video") {
          this.qrcodeData = `${host}/qrcode/${this.data.documentArchiveId}/video`;
        } else if (this.data.documentId === ""
          && this.data.documentKey === ""
          && this.data.subDocumentId === ""
          && this.data.subStructureRoute === ""
          && this.data.subStructure === "" ) {
          this.qrcodeData = `${host}/qrcode/${this.data.documentArchiveId}/docstructure/${section}`;
          // this.qrcodeData = JSON.stringify({
          //   archive: this.data.documentArchiveId,
          //   section,
          // });
        } else if (this.data.documentId === ""
          && this.data.documentKey === ""
          && this.data.subDocumentId === ""
          && this.data.subStructureRoute !== ""
          && this.data.subStructure !== ""
          && this.data.structureId === "" ) {
          this.qrcodeData = `${host}/qrcode/${this.data.documentArchiveId}/docsubstructure/${section}/${this.data.subStructureRoute}`;
        } else if (this.data.documentId === ""
          && this.data.documentKey === ""
          && this.data.subDocumentId === ""
          && this.data.subStructureRoute !== ""
          && this.data.subStructure !== ""
          && this.data.structureId !== "" ) {
          this.qrcodeData = `${host}/qrcode/${this.data.documentArchiveId}/docsubstructure/${section}/${this.data.subStructureRoute}/${this.data.structureId}`;
        } else {
          this.qrcodeData =
            `${host}/qrcode/${this.data.documentArchiveId}/${section}/${documentId}/${this.data.documentKey}/${this.data.subDocumentId}`;

        }
      }
    } else {
      // this.toastrService.error('Errore nel recupero dei dati');
      // this.onClose(null);
    }
  }

  /**
   * Handle dialog closing
   *
   * @since 1.0.0
   */
  onClose(): void {
    this.dialogRef.close();
  }

  onPrint(): void {
    const printData = document.getElementById('dataToPrint').innerHTML//.cloneNode(true);

    // document.body.appendChild(printData);
    // window.print();
    // document.body.removeChild(printData);
    const openWindow = window.open("", "title", "attributes");
    openWindow.document.write(printData);
    openWindow.document.close();
    openWindow.focus();
    openWindow.print();
    openWindow.close();
  }

  
  /**
   * Convert image into Base64
   *
   * @since 1.0.0
   */
   private convertBase64ToBlob(Base64Image: any): Blob {
    // SPLIT INTO TWO PARTS
    const parts = Base64Image.split(';base64,');
    // HOLD THE CONTENT TYPE
    const imageType = parts[0].split(':')[1];
    // DECODE BASE64 STRING
    const decodedData = window.atob(parts[1]);
    // CREATE UNIT8ARRAY OF SIZE SAME AS ROW DATA LENGTH
    const uInt8Array = new Uint8Array(decodedData.length);
    // INSERT ALL CHARACTER CODE INTO UINT8ARRAY
    for (let i = 0; i < decodedData.length; ++i) {
      uInt8Array[i] = decodedData.charCodeAt(i);
    }
    // RETURN BLOB IMAGE AFTER CONVERSION
    return new Blob([uInt8Array], { type: imageType });
  }

  /**
   * Handle save QR code as image
   *
   * @since 1.0.0
   */
   onSaveQRAsImage(parent): void {
    // fetches base 64 date from image
    const parentElement = parent.qrcElement.nativeElement.querySelector("img").src;

    // converts base 64 encoded image to blobData
    const blobData = this.convertBase64ToBlob(parentElement);

    // saves as image
    if (window.navigator && window.navigator.msSaveOrOpenBlob) { //IE
      window.navigator.msSaveOrOpenBlob(blobData, 'Qrcode');
    } else { // chrome
      const blob = new Blob([blobData], { type: "image/png" });
      const url = window.URL.createObjectURL(blob);
      // window.open(url);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Qrcode.png';
      link.click();
    }
  }

}
